/**
 * Smooths loading-bar updates so the UI keeps moving during stage gaps
 * (PrepareResources / ResourcesLoaded) when no files are downloading.
 */
export class SmoothLoadProgress {
    protected realPercent = 0;
    protected displayPercent = 0;
    protected rafId = 0;
    protected lastTick = 0;
    protected readonly onDisplay: (percent: number) => void;
    /** How far display may creep past real while stalled (one stage is ~14%). */
    protected readonly stallCreepCap = 12;
    /** Percent points per second while waiting on CPU work between downloads. */
    protected readonly stallSpeed = 4;
    protected readonly catchUpSpeed = 10;

    public constructor(onDisplay: (percent: number) => void) {
        this.onDisplay = onDisplay;
    }

    public SetReal(percent: number): void {
        this.realPercent = Math.max(this.realPercent, Math.min(100, percent));
        this.ensureTicker();
    }

    public Complete(): void {
        this.realPercent = 100;
        this.ensureTicker();
    }

    public Stop(): void {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = 0;
        }
    }

    protected ensureTicker(): void {
        if (this.rafId) {
            return;
        }
        this.lastTick = performance.now();
        const tick = (now: number) => {
            const dt = Math.min(0.1, (now - this.lastTick) / 1000);
            this.lastTick = now;

            if (this.realPercent >= 100) {
                this.displayPercent = Math.min(100, this.displayPercent + dt * 50);
            } else if (this.displayPercent < this.realPercent) {
                const delta = this.realPercent - this.displayPercent;
                this.displayPercent += Math.min(delta, Math.max(delta * dt * this.catchUpSpeed, dt * 8));
            } else {
                // Stalled on real progress: keep bar moving toward a soft cap under the next stage.
                const softCap = Math.min(99, this.realPercent + this.stallCreepCap);
                if (this.displayPercent < softCap) {
                    this.displayPercent = Math.min(softCap, this.displayPercent + dt * this.stallSpeed);
                }
            }

            this.onDisplay(this.displayPercent);

            if (this.displayPercent < 100 || this.realPercent < 100) {
                this.rafId = requestAnimationFrame(tick);
            } else {
                this.rafId = 0;
                this.onDisplay(100);
            }
        };
        this.rafId = requestAnimationFrame(tick);
    }
}
